import { useCallback, useEffect, useRef } from "react"

import {
    extendOrRefreshAccessToken,
    revokeAccessToken,
    subscribeToTokenDeath,
} from "../../utils"

const KEEP_ALIVE_REFRESH_INTERVAL =
    Number(process.env.REACT_APP_KEEP_ALIVE_INTERVAL_MINS) * 60 || 5 * 60

export interface UseAccessTokenManagementProps {
    baseUrl?: string
    disabled?: boolean
    onTokenDeath(): void
    refreshIntervalSeconds?: number
    refreshThresholdSeconds?: number
}
export interface AccessTokenManagementResponse {
    revokeToken: () => void
}

/**
 * React hook that handles keeping an access token alive for as long as a user is active and returns a revoke function that will handle removing the
 * token manually when called.
 *
 *
 * | Env Variable                                    | Description                                                                                 |
 * | ----------------------------------------------- | ------------------------------------------------------------------------------------------- |
 * | <code>REACT_APP_SERVER_BASE</code>              | Overrides the default `baseUrl`                                                             |
 * | <code>REACT_APP_REFRESH_THRESHOLD_MINS</code>   | Overrides the default `refreshThresholdSeconds`; however, value is in minutes, not seconds. |
 * | <code>REACT_APP_KEEP_ALIVE_INTERVAL_MINS</code> | Overrides the default `refreshIntervalSeconds`; however, value is in minutes, not seconds.  |
 *
 * @param {object} options Configuration options
 * @param {string} [options.baseUrl] Base Url of API server. Will use the <code>REACT_APP_SERVER_BASE</code> env variable by default.
 * @param {object} [options.disabled=false] Whether calls to extend or refresh token should be disabled. Default is `false`.
 * @param {object} options.onTokenDeath Handler function with actions to perform when an access token expires.
 * @param {object} [options.refreshIntervalSeconds=300] How often to trigger token keep alive. Default is `300`.
 * @param {object} [options.refreshThresholdSeconds] Defines the maximum TTL allowed before we request a new token. Will use the <code>REACT_APP_KEEP_ALIVE_INTERVAL_MINS</code> by default.
 * @returns {AccessTokenManagementResponse} Object with a revokeToken function that can be called to clear token and trigger onTokenDeath.
 *
 * @example
 *
 * ```typescript
 * const App:FC = () => {
 *
 *  // Initialise the hook and get the revoke function
 *  const { revokeToken } = useAccessTokenManagement({
 *      onTokenDeath: () => {
 *          window.location.href = "/signout"
 *      }
 *  })
 *
 *  // Hook the revokeToken method up to a sign out button
 *  return (
 *      <button onClick={revokeToken}>Sign Out</button>
 *  )
 * }
 * ```
 */
const useAccessTokenManagement = ({
    baseUrl,
    disabled = false,
    onTokenDeath,
    refreshIntervalSeconds = KEEP_ALIVE_REFRESH_INTERVAL,
    refreshThresholdSeconds,
}: UseAccessTokenManagementProps): AccessTokenManagementResponse => {
    // Make sure refreshIntervalSeconds is set to a value in case it's passed as undefined
    refreshIntervalSeconds =
        refreshIntervalSeconds ?? KEEP_ALIVE_REFRESH_INTERVAL

    const tokenDeathHandler = useRef(onTokenDeath)
    const extendOrRefreshAccessTokenWithProps = useCallback(() => {
        return extendOrRefreshAccessToken({ baseUrl, refreshThresholdSeconds })
    }, [baseUrl, refreshThresholdSeconds])

    // Initial call
    useEffect(() => {
        if (!disabled) extendOrRefreshAccessTokenWithProps()
    }, [disabled, extendOrRefreshAccessTokenWithProps])

    useEffect(() => {
        if (disabled) return

        const timer = window.setInterval(
            extendOrRefreshAccessTokenWithProps,
            refreshIntervalSeconds * 1000
        )

        return () => window.clearInterval(timer)
    }, [disabled, extendOrRefreshAccessTokenWithProps, refreshIntervalSeconds])

    useEffect(() => {
        tokenDeathHandler.current = onTokenDeath
    }, [onTokenDeath])

    useEffect(() => {
        subscribeToTokenDeath(
            () => tokenDeathHandler.current && tokenDeathHandler.current()
        )
    }, [tokenDeathHandler])

    return {
        revokeToken: () => {
            revokeAccessToken({ baseUrl })
        },
    }
}

export default useAccessTokenManagement

/**
 * Response object from useAccessTokenManagement hook, providing helper functions for manual token manipulation.
 *
 * @typedef {object} AccessTokenManagementResponse
 * @property {Function} revokeToken Revokes an access token and triggers the onTokenDeath handler
 */
